import * as React from "react";
import { cn } from "../../../lib/utils";
import {
  Globe2,
  AlertTriangle,
  Server,
  Shield,
  Copy,
  CheckCircle2,
  Download,
  List,
  GitBranch,
  Lock,
  Unlock,
  XCircle,
} from "lucide-react";
import { useState, useMemo } from "react";
import { ModuleContainer, ModuleSection } from "../ModuleContainer";
import { IssuesList, type Issue } from "../IssuesList";
import { ModuleDataTable, type ColumnDef } from "../ModuleDataTable";
import { Badge } from "../../badge";
import { Button } from "../../button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../card";

// Types
interface SubdomainInfo {
  subdomain: string;
  sources: string[];
  ips: string[];
  // New fields for PRD gaps
  http_status?: number;
  https_status?: number;
  tls_valid?: boolean;
  tls_expires?: string;
  redirect_url?: string;
}

interface TakeoverFinding {
  subdomain: string;
  cname?: string;
  service?: string;
  vulnerability?: string;
  confidence?: "high" | "medium" | "low";
}

export interface SubdomainsModuleData {
  score?: number;
  target?: string;
  subdomains?: SubdomainInfo[];
  total_found?: number;
  sources_used?: string[];
  takeover_findings?: TakeoverFinding[];
  issues?: Array<string | Issue>;
}

export interface SubdomainsModuleViewProps {
  data: SubdomainsModuleData;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  className?: string;
  /** Hide the header (useful when rendered within tabs) */
  hideHeader?: boolean;
}

// Source colors
const SOURCE_COLORS: Record<string, string> = {
  ct_logs: "bg-purple-500/10 text-purple-400 border-purple-500/30",
  crtsh: "bg-purple-500/10 text-purple-400 border-purple-500/30",
  bruteforce: "bg-cyan-500/10 text-cyan-400 border-cyan-500/30",
  dns: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  hackertarget: "bg-orange-500/10 text-orange-400 border-orange-500/30",
  urlscan: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  securitytrails: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  virustotal: "bg-red-500/10 text-red-400 border-red-500/30",
};

// Confidence colors
const CONFIDENCE_COLORS: Record<string, string> = {
  high: "bg-red-500/15 text-red-400 border-red-500/40",
  medium: "bg-amber-500/15 text-amber-400 border-amber-500/40",
  low: "bg-cyan-500/15 text-cyan-400 border-cyan-500/40",
};

// Normalize issues
function normalizeIssues(issues?: Array<string | Issue>): Issue[] {
  if (!issues) return [];
  return issues.map((issue, index) => {
    if (typeof issue === "string") {
      let severity: Issue["severity"] = "info";
      if (issue.toLowerCase().includes("takeover") || issue.toLowerCase().includes("vulnerable")) {
        severity = "critical";
      } else if (issue.toLowerCase().includes("dangling") || issue.toLowerCase().includes("orphan")) {
        severity = "high";
      }
      return { id: `subdomains-issue-${index}`, severity, message: issue, module: "subdomains" };
    }
    return issue;
  });
}

// Copy button component
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="p-1 rounded hover:bg-muted transition-colors"
      title="Copy to clipboard"
    >
      {copied ? (
        <CheckCircle2 className="h-3 w-3 text-emerald-400" />
      ) : (
        <Copy className="h-3 w-3 text-muted-foreground" />
      )}
    </button>
  );
}

// Export subdomains as CSV
function exportSubdomainsCsv(subdomains: SubdomainInfo[], target?: string) {
  const headers = ["Subdomain", "IPs", "HTTP Status", "HTTPS Status", "TLS Valid", "TLS Expires", "Sources"];
  const csvContent = [
    headers.join(","),
    ...subdomains.map((s) => [
      `"${s.subdomain}"`,
      `"${s.ips.join("; ")}"`,
      s.http_status || "",
      s.https_status || "",
      s.tls_valid !== undefined ? (s.tls_valid ? "Yes" : "No") : "",
      `"${s.tls_expires || ""}"`,
      `"${s.sources.join("; ")}"`,
    ].join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `subdomains_${target || "scan"}_${new Date().toISOString().split("T")[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Export as simple text list (one subdomain per line)
function exportSubdomainsList(subdomains: SubdomainInfo[], target?: string) {
  const content = subdomains.map((s) => s.subdomain).join("\n");
  const blob = new Blob([content], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `subdomains_${target || "scan"}_${new Date().toISOString().split("T")[0]}.txt`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// HTTP Status Badge
function HttpStatusBadge({ status }: { status?: number }) {
  if (!status) return <span className="text-xs text-muted-foreground">-</span>;

  const getStatusColor = (code: number) => {
    if (code >= 200 && code < 300) return "bg-emerald-500/10 text-emerald-400 border-emerald-500/30";
    if (code >= 300 && code < 400) return "bg-cyan-500/10 text-cyan-400 border-cyan-500/30";
    if (code >= 400 && code < 500) return "bg-amber-500/10 text-amber-400 border-amber-500/30";
    if (code >= 500) return "bg-red-500/10 text-red-400 border-red-500/30";
    return "bg-muted text-muted-foreground";
  };

  return (
    <Badge variant="outline" className={cn("text-xs font-mono", getStatusColor(status))}>
      {status}
    </Badge>
  );
}

// TLS Status Badge
function TlsStatusBadge({ valid, expires }: { valid?: boolean; expires?: string }) {
  if (valid === undefined) return <span className="text-xs text-muted-foreground">-</span>;

  const isExpiringSoon = expires && new Date(expires) <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

  if (!valid) {
    return (
      <Badge variant="outline" className="text-xs bg-red-500/10 text-red-400 border-red-500/30 gap-1">
        <XCircle className="h-3 w-3" />
        Invalid
      </Badge>
    );
  }

  if (isExpiringSoon) {
    return (
      <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-400 border-amber-500/30 gap-1">
        <Unlock className="h-3 w-3" />
        Expiring
      </Badge>
    );
  }

  return (
    <Badge variant="outline" className="text-xs bg-emerald-500/10 text-emerald-400 border-emerald-500/30 gap-1">
      <Lock className="h-3 w-3" />
      Valid
    </Badge>
  );
}

// Tree View Node Interface
interface TreeNode {
  name: string;
  fullPath: string;
  children: Map<string, TreeNode>;
  data?: SubdomainInfo;
  count: number;
}

// Build tree from subdomains
function buildSubdomainTree(subdomains: SubdomainInfo[], baseDomain: string): TreeNode {
  const root: TreeNode = {
    name: baseDomain,
    fullPath: baseDomain,
    children: new Map(),
    count: 0,
  };

  subdomains.forEach((sub) => {
    const subdomain = sub.subdomain;
    // Remove base domain to get prefix parts
    const prefix = subdomain.endsWith(`.${baseDomain}`)
      ? subdomain.slice(0, -(baseDomain.length + 1))
      : subdomain === baseDomain
        ? ""
        : subdomain;

    if (!prefix) {
      root.data = sub;
      root.count++;
      return;
    }

    // Split and reverse to build tree from root
    const parts = prefix.split(".").reverse();
    let current = root;

    parts.forEach((part, index) => {
      const isLast = index === parts.length - 1;
      if (!current.children.has(part)) {
        current.children.set(part, {
          name: part,
          fullPath: [...parts.slice(0, index + 1).reverse(), baseDomain].join("."),
          children: new Map(),
          count: 0,
        });
      }
      current = current.children.get(part)!;
      if (isLast) {
        current.data = sub;
      }
      current.count++;
    });
  });

  return root;
}

// Tree View Component
function SubdomainTreeView({ root, depth = 0 }: { root: TreeNode; depth?: number }) {
  const [expanded, setExpanded] = React.useState(depth < 2);

  if (root.children.size === 0 && !root.data) return null;

  const hasChildren = root.children.size > 0;
  const childrenArray = Array.from(root.children.values()).sort((a, b) => b.count - a.count);

  return (
    <div className={cn("border-l border-border", depth > 0 && "ml-4 pl-3")}>
      <div
        className={cn(
          "flex items-center gap-2 py-1.5 px-2 rounded hover:bg-muted/50 transition-colors",
          hasChildren && "cursor-pointer"
        )}
        onClick={() => hasChildren && setExpanded(!expanded)}
      >
        {hasChildren ? (
          <span className="text-muted-foreground text-xs">{expanded ? "▼" : "▶"}</span>
        ) : (
          <span className="w-3" />
        )}
        <code className="text-sm font-mono">{root.name}</code>
        {root.data && (
          <>
            {root.data.ips.length > 0 && (
              <code className="text-xs text-muted-foreground bg-muted/50 px-1.5 rounded">
                {root.data.ips[0]}
              </code>
            )}
            <TlsStatusBadge valid={root.data.tls_valid} expires={root.data.tls_expires} />
          </>
        )}
        {hasChildren && (
          <Badge variant="outline" className="text-xs ml-auto">
            {root.count}
          </Badge>
        )}
      </div>
      {expanded && childrenArray.map((child) => (
        <SubdomainTreeView key={child.name} root={child} depth={depth + 1} />
      ))}
    </div>
  );
}

export function SubdomainsModuleView({
  data,
  isLoading,
  error,
  onRetry,
  className,
  hideHeader,
}: SubdomainsModuleViewProps) {
  const [viewMode, setViewMode] = useState<"list" | "tree">("list");
  const issues = normalizeIssues(data.issues);
  const subdomains = data.subdomains || [];
  const takeoverFindings = data.takeover_findings || [];
  const sourcesUsed = data.sources_used || [];

  // Count by source
  const sourceCounts = subdomains.reduce((acc, sub) => {
    sub.sources.forEach(source => {
      acc[source] = (acc[source] || 0) + 1;
    });
    return acc;
  }, {} as Record<string, number>);

  // Count with IPs
  const withIps = subdomains.filter(s => s.ips.length > 0).length;

  // Unique IPs
  const uniqueIps = new Set(subdomains.flatMap(s => s.ips));

  // Build tree view
  const subdomainTree = useMemo(() => {
    if (!data.target) return null;
    return buildSubdomainTree(subdomains, data.target);
  }, [subdomains, data.target]);

  // Subdomain columns with HTTP and TLS status
  const columns: ColumnDef<SubdomainInfo>[] = [
    {
      id: "subdomain",
      header: "Subdomain",
      accessor: (row) => row.subdomain,
      cell: (value) => (
        <div className="flex items-center gap-2">
          <code className="text-sm font-mono">{value as string}</code>
          <CopyButton text={value as string} />
        </div>
      ),
      sortable: true,
      filterable: true,
    },
    {
      id: "ips",
      header: "IP Addresses",
      accessor: (row) => row.ips.join(", "),
      cell: (_, row) => (
        <div className="flex flex-wrap gap-1">
          {row.ips.length > 0 ? (
            row.ips.slice(0, 3).map((ip, i) => (
              <code key={i} className="text-xs font-mono bg-muted/50 px-1.5 py-0.5 rounded">
                {ip}
              </code>
            ))
          ) : (
            <span className="text-xs text-muted-foreground">No resolution</span>
          )}
          {row.ips.length > 3 && (
            <Badge variant="outline" className="text-xs">+{row.ips.length - 3}</Badge>
          )}
        </div>
      ),
      sortable: true,
    },
    {
      id: "http",
      header: "HTTP",
      accessor: (row) => row.http_status || row.https_status || 0,
      cell: (_, row) => (
        <div className="flex gap-1">
          {row.http_status && <HttpStatusBadge status={row.http_status} />}
          {row.https_status && (
            <Badge variant="outline" className={cn("text-xs font-mono gap-1", row.https_status >= 200 && row.https_status < 400 ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" : "bg-muted")}>
              <Lock className="h-2.5 w-2.5" />
              {row.https_status}
            </Badge>
          )}
          {!row.http_status && !row.https_status && <span className="text-xs text-muted-foreground">-</span>}
        </div>
      ),
      sortable: true,
      width: "120px",
    },
    {
      id: "tls",
      header: "TLS",
      accessor: (row) => row.tls_valid ? 1 : 0,
      cell: (_, row) => <TlsStatusBadge valid={row.tls_valid} expires={row.tls_expires} />,
      sortable: true,
      width: "100px",
    },
    {
      id: "sources",
      header: "Sources",
      accessor: (row) => row.sources.join(", "),
      cell: (_, row) => (
        <div className="flex flex-wrap gap-1">
          {row.sources.map((source, i) => (
            <Badge
              key={i}
              variant="outline"
              className={cn("text-xs", SOURCE_COLORS[source.toLowerCase()] || "bg-muted")}
            >
              {source}
            </Badge>
          ))}
        </div>
      ),
      filterable: true,
    },
  ];

  return (
    <ModuleContainer
      moduleName="subdomains"
      icon={Globe2}
      data={data}
      isLoading={isLoading}
      error={error}
      score={data.score}
      onRetry={onRetry}
      className={className}
      hideHeader={hideHeader}
    >
      <div className="space-y-6">
        {/* Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal">
                Subdomains Found
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-mono text-cyan-400">
                {data.total_found || subdomains.length}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal">
                With Resolution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-mono">{withIps}</p>
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal">
                Unique IPs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-mono">{uniqueIps.size}</p>
            </CardContent>
          </Card>
          <Card className={cn("bg-card/50", takeoverFindings.length > 0 && "border-red-500/30")}>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal flex items-center gap-1">
                <Shield className="h-3 w-3 text-red-400" /> Takeover Risks
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className={cn("text-2xl font-bold font-mono", takeoverFindings.length > 0 ? "text-red-400" : "text-emerald-400")}>
                {takeoverFindings.length}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Takeover Alert */}
        {takeoverFindings.length > 0 && (
          <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 animate-pulse">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-red-400" />
              <div>
                <h4 className="font-semibold text-red-400">Subdomain Takeover Vulnerability Detected</h4>
                <p className="text-sm text-muted-foreground">
                  {takeoverFindings.length} subdomain(s) may be vulnerable to takeover attacks. Immediate action required.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Sources Used */}
        {sourcesUsed.length > 0 && (
          <div className="p-4 rounded-lg bg-muted/30 border border-border">
            <p className="text-xs text-muted-foreground mb-2">Discovery Sources</p>
            <div className="flex flex-wrap gap-2">
              {sourcesUsed.map((source, index) => (
                <Badge
                  key={index}
                  variant="outline"
                  className={cn("text-xs", SOURCE_COLORS[source.toLowerCase()] || "bg-muted")}
                >
                  {source}
                  {sourceCounts[source] && (
                    <span className="ml-1 opacity-70">({sourceCounts[source]})</span>
                  )}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Takeover Findings */}
        {takeoverFindings.length > 0 && (
          <ModuleSection
            title="Subdomain Takeover Risks"
            icon={Shield}
            badge={<Badge variant="outline" className="text-xs bg-red-500/10 border-red-500/30 text-red-400">{takeoverFindings.length}</Badge>}
          >
            <div className="space-y-3">
              {takeoverFindings.map((finding, index) => (
                <div key={index} className="p-4 rounded-lg bg-red-500/5 border border-red-500/30">
                  <div className="flex items-start justify-between">
                    <div>
                      <code className="text-sm font-mono font-semibold text-red-400">
                        {finding.subdomain}
                      </code>
                      {finding.cname && (
                        <p className="text-xs text-muted-foreground mt-1">
                          CNAME: <code className="font-mono">{finding.cname}</code>
                        </p>
                      )}
                      {finding.vulnerability && (
                        <p className="text-sm mt-2">{finding.vulnerability}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {finding.service && (
                        <Badge variant="outline" className="text-xs">{finding.service}</Badge>
                      )}
                      {finding.confidence && (
                        <Badge variant="outline" className={cn("text-xs", CONFIDENCE_COLORS[finding.confidence])}>
                          {finding.confidence} confidence
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ModuleSection>
        )}

        {/* Subdomains Section */}
        {subdomains.length > 0 && (
          <ModuleSection
            title="Discovered Subdomains"
            icon={Server}
            badge={<Badge variant="outline" className="text-xs">{subdomains.length}</Badge>}
          >
            {/* Toolbar */}
            <div className="flex items-center justify-between gap-4 mb-4">
              {/* View Toggle */}
              <div className="flex items-center gap-1 p-1 bg-muted/30 rounded-lg">
                <Button
                  variant={viewMode === "list" ? "secondary" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("list")}
                  className="gap-1.5 h-7"
                >
                  <List className="h-3.5 w-3.5" />
                  List
                </Button>
                <Button
                  variant={viewMode === "tree" ? "secondary" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("tree")}
                  className="gap-1.5 h-7"
                  disabled={!subdomainTree}
                >
                  <GitBranch className="h-3.5 w-3.5" />
                  Tree
                </Button>
              </div>

              {/* Export Buttons */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportSubdomainsList(subdomains, data.target)}
                  className="gap-1.5"
                >
                  <Download className="h-4 w-4" />
                  TXT
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportSubdomainsCsv(subdomains, data.target)}
                  className="gap-1.5"
                >
                  <Download className="h-4 w-4" />
                  CSV
                </Button>
              </div>
            </div>

            {/* View Content */}
            {viewMode === "tree" && subdomainTree ? (
              <div className="border border-border rounded-lg p-4 max-h-[500px] overflow-auto">
                <SubdomainTreeView root={subdomainTree} />
              </div>
            ) : (
              <ModuleDataTable
                data={subdomains}
                columns={columns}
                paginated={subdomains.length > 15}
                searchable
                searchPlaceholder="Search subdomains..."
              />
            )}
          </ModuleSection>
        )}

        {/* Issues */}
        {issues.length > 0 && (
          <ModuleSection
            title="Issues"
            badge={<Badge variant="outline" className="text-xs bg-amber-500/10 border-amber-500/30 text-amber-400">{issues.length}</Badge>}
          >
            <IssuesList issues={issues} groupBySeverity showCounts />
          </ModuleSection>
        )}
      </div>
    </ModuleContainer>
  );
}

SubdomainsModuleView.displayName = "SubdomainsModuleView";
